import { FieldType } from 'bindings/forms/FieldType';
import * as moment from 'moment';
import { IAdditionalColumnsSubValue } from 'proto/protobufs';
import * as React from 'react';
import { PieChart } from 'react-minimal-pie-chart';
import { DataEntry } from 'react-minimal-pie-chart/types/commonTypes';
import { AdditionalColumnType, ReportColumnType } from '../../atoms/additionalColumns';
import { AssortmentShort } from '../../atoms/assortment';
import { getUserColor } from '../../components_v2/avatar/Data/Data';
import { SurfaceColor } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';
import { getColorForPercentage, getCurrencyFormatter, getNumberFormatter } from '../reports/utils';
import { PinStyle } from '../settings/subSettings/actions';
import { DEFAULT_MAP_PIN_COLOR, getPinColorFromRange } from './cluster';
import { ColorScale, ColumnDisplayOptions, ScaleRange, booleanColorsOf, bucketOf, colorOf, daysFromToday, formatDaysFromToday, percentOf, scaleBucketLabel, scaleBuckets } from './colorScale';
import { MinMaxAdditionalColumn } from './MapView';
import { ClusterProperties, MapCompany } from './model';
import MapToolTip from './toolTip';

type FnReturn = Omit<DataEntry, 'value'> & { color: string };

function calcPercentage(value: { numer: number, denom: number }) {
	return (value.numer / value.denom) * 100;
}

// A configured colour scale replaces the historical buckets: the pin takes
// the colour of its value along the scale, the cluster pie and the legend
// list the scale's slices. The range the scale spans is the one set on the
// column, else the values on the map, which may be unset when nothing
// numeric was loaded (hence the fallback, 0 to 100 for a percentage).
function rangeOnMap(minMax: { min: number, max: number } | undefined, fallback: ScaleRange = { min: 0, max: 0 }): ScaleRange {
	if (!minMax || minMax.min > minMax.max) return fallback;
	return { min: minMax.min, max: minMax.max };
}

function scaledEntry(scale: ColorScale, value: number, onMap: ScaleRange, format: (value: number) => string, dates = false): FnReturn {
	const bucket = bucketOf(scaleBuckets(scale, onMap), value);
	return { label: scaleBucketLabel(bucket, format, dates), color: bucket.color };
}

const PERCENT_RANGE: ScaleRange = { min: 0, max: 100 };

// a date column's bounds on the map are days from today, as a scale reads them
function daysOnMap(minMaxAdditionalColumn: MinMaxAdditionalColumn[], id: string, field: boolean): ScaleRange {
	return rangeOnMap(minMaxAdditionalColumn.find(mm => mm.id === id && mm.field === field));
}
const formatPercent = (value: number) => `${parseFloat(value.toFixed(2))}%`;

// A per-product calculated field stores { products: [[uuid, value], ...] }:
// the map colors by a single product, so narrow the sub value down to the
// selected product's entry before the regular coloring/min-max paths run
export function pickProductValue(item: IAdditionalColumnsSubValue | null | undefined, productUuid: string | undefined): IAdditionalColumnsSubValue | null | undefined {
	if (!item || item.value == null) return item;
	try {
		const parsed = JSON.parse(item.value);
		if (parsed && typeof parsed === 'object' && 'products' in parsed) {
			const entry = (parsed.products as [string, unknown][]).find(([uuid]) => uuid === productUuid);
			return entry ? { ...item, value: JSON.stringify(entry[1]) } : undefined;
		}
	} catch (_) { /* not JSON: a plain value, kept as is */ }
	return item;
}

export function aggregateNumberField(item: IAdditionalColumnsSubValue | null | undefined, acc: MinMaxAdditionalColumn) {
	let val;
	if (!item || item.value === 'empty') return;
	try {
		val = JSON.parse(item?.value ?? '');
		if (typeof val === 'number') {
			if (isNaN(val)) return;
			acc.min = Math.min(val, acc.min);
			acc.max = Math.max(val, acc.max);
			return;
		}
		const value = val as ReportColumnType;
		if (value === null) return;
		if ('int' in value) {
			if (isNaN(value.int)) return;
			acc.min = Math.min(value.int, acc.min);
			acc.max = Math.max(value.int, acc.max);
		} else if ('float' in value) {
			if (isNaN(value.float)) return;
			acc.min = Math.min(value.float, acc.min);
			acc.max = Math.max(value.float, acc.max);
		} else if ('percentage' in value) {
			if (isNaN(value.percentage.numer) || isNaN(value.percentage.denom) || value.percentage.denom === 0) return;
			const perc = calcPercentage(value.percentage);
			acc.min = Math.min(perc, acc.min);
			acc.max = Math.max(perc, acc.max);
		} else if ('evolution' in value && value.evolution.new.val && 'percentage' in value.evolution.new.val) {
			const perc = calcPercentage(value.evolution.new.val.percentage);
			acc.min = Math.min(perc, acc.min);
			acc.max = Math.max(perc, acc.max);
		}
	} catch (_) {
		const date = Date.parse(item.value ?? '');
		if (!isNaN(date)) {
			const range = moment.utc(date).diff(moment(), 'days');
			acc.min = Math.min(range, acc.min);
			acc.max = Math.max(range, acc.max);
		}
		return;
	}
}

function getColorForAdditionalColumnDate(value: string, minMaxAdditionalColumn: MinMaxAdditionalColumn[], id: string, field: boolean, rangeColor: { firstRange: number, lastRange: number }): string {
	const range = moment.utc(value).diff(moment(), 'days');
	const { min, max } = minMaxAdditionalColumn.find(minMax => minMax.id === id && minMax.field === field) ?? { min: undefined, max: undefined, isMoney: false };
	const minMax = min != null && max != null ? { firstRange: min, lastRange: max } : rangeColor;
	const absMax = range < 0 ? Math.abs(minMax.firstRange) : Math.abs(minMax.lastRange);
	const perc = Math.floor((Math.abs(range) / absMax) * 10);
	return `color-mix(in oklab, ${range < 0 ? 'rgb(162, 21, 9)' : 'rgb(5, 119, 57)'} ${perc * 10}%, ${SurfaceColor})`;

}

export function genAdditionalColumnClusterData(
	points: ClusterProperties[],
	pointToValue: (point: ClusterProperties) => IAdditionalColumnsSubValue | null | undefined,
	type: AdditionalColumnType | FieldType | 'CalculatedField',
	rangeColor: { firstRange: number, lastRange: number },
	assortiments: AssortmentShort[],
	minMaxAdditionalColumn: MinMaxAdditionalColumn[],
	field: boolean,
	id: string,
	customPins?: Record<string, PinStyle>,
	displayOptions?: ColumnDisplayOptions,
): { data: DataEntry[], noToolType?: boolean } {
	const colorScale = displayOptions?.color_scale;
	let fn: (value: string | null | undefined, name: string | null | undefined) => FnReturn | Array<FnReturn>;
	let noToolType = false;
	const noDataOption = { label: translateToString('no data'), color: DEFAULT_MAP_PIN_COLOR };

	switch (type) {
		case 'Company':
			fn = (_, name) => {
				if (name === undefined || name === null) return noDataOption;
				return { label: name, color: getUserColor(name) };
			};
			break;
		case 'Boolean':
			fn = (value) => {
				if (value === undefined || value === null) return noDataOption;
				const parsed = JSON.parse(value);
				const colors = booleanColorsOf(displayOptions);
				if (parsed === false) return { label: translateToString('false'), color: colors.false };
				return { label: translateToString('true'), color: colors.true };
			};
			break;
		case 'Date':
			fn = (value) => {
				if (value === undefined || value === null) return noDataOption;
				if (colorScale) return scaledEntry(colorScale, daysFromToday(value), daysOnMap(minMaxAdditionalColumn, id, field), formatDaysFromToday, true);
				const color = getColorForAdditionalColumnDate(value, minMaxAdditionalColumn, id, field, rangeColor);
				return { label: color, color };
			};
			noToolType = !colorScale;
			break;
		case 'Catalogue':
			fn = value => {
				if (value === undefined || value === null) return noDataOption;
				const parsed: number = JSON.parse(value);
				const assortment = assortiments.find(a => a.id === parsed);
				return { label: assortment?.name ?? '', color: getUserColor(assortment?.name ?? '') };
			};
			break;
		case 'String':
		case 'Select':
			fn = value => {
				if (value === undefined || value === null) return noDataOption;
				return { label: value, color: customPins ? customPins[value]?.color ?? getUserColor(value) : getUserColor(value) };
			};
			break;
		case 'MultiSelect':
		case 'Multiselect':
			fn = value => {
				if (value === undefined || value === null) return noDataOption;
				const parsed: string[] = JSON.parse(value);
				return parsed.map(p => ({ label: p, color: customPins ? customPins[p]?.color ?? getUserColor(p) : getUserColor(p) }));
			};
			break;
		case 'Integer':
		case 'Money':
		case 'Number': {

			const { min, max, isMoney } = minMaxAdditionalColumn.find(minMax => minMax.id === id && field === minMax.field) ?? { min: 0, max: 0, isMoney: false };
			const steps = [min, ...[2, 3, 4].map(n => min + max - ((max - min) / n)), max];
			const formatter = isMoney ? getCurrencyFormatter() : getNumberFormatter();
			const divideForMoney = (value: number) => isMoney ? value / 100 : value;
			const onMap = rangeOnMap({ min: divideForMoney(min), max: divideForMoney(max) });

			fn = value => {
				if (value === undefined || value === null) return noDataOption;
				const parsed: number = JSON.parse(value);
				if (colorScale) {
					return scaledEntry(colorScale, divideForMoney(parsed), onMap, v => formatter.format(v));
				}
				if (min === max) {
					return { label: formatter.format(divideForMoney(min)), color: 'rgb(5, 119, 57)' };
				} else {
					const { color, stepIndex } = getColorAndStepBySteps(parsed, steps, { r: 255, g: 255, b: 255 }, { r: 5, g: 119, b: 57 });
					return { label: translateToString('from_{{NAME1}}_to_{{NAME2}}', [['NAME1', stepIndex === 0 ? '-inf' : formatter.format(divideForMoney(steps[stepIndex - 1]))], ['NAME2', formatter.format(divideForMoney(steps[stepIndex]))]]), color };
				}
			};
			break;
		}
		case 'CalculatedField':
		case 'ReportColumn':
			fn = comming => {
				if (comming === undefined || comming === null || comming === 'empty') return noDataOption;
				const value: ReportColumnType = JSON.parse(comming);
				if (typeof value === 'string' || !value) {
					return noDataOption;
				}
				if ('percentage' in value) {
					if (colorScale) {
						return scaledEntry(colorScale, percentOf(value.percentage), rangeOnMap(minMaxAdditionalColumn.find(minMax => minMax.id === id && field === minMax.field), PERCENT_RANGE), formatPercent);
					}
					const percentage = value.percentage[0] / value.percentage[1];
					const steps = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
					const colors = ['#d44b39', '#F7685B', '#FF814B', '#FEA240', '#FEC940', '#FFEA2C', '#D2F34E', '#87F16D', '#2ED47A', '#44b772', '#45975a'];
					const { color, stepIndex } = getColorDiscreetSteps(percentage, steps, colors);
					return { label: translateToString('from_{{NAME1}}_to_{{NAME2}}', [['NAME1', stepIndex === 0 ? '-inf' : `${steps[stepIndex - 1] * 100}%`], ['NAME2', `${steps[stepIndex] * 100}%`]]), color };
				}
				if ('text' in value) {
					return { label: value.text, color: getUserColor(value.text) };
				}
				if ('key' in value) {
					const translated = translateToString(value.key);
					return { label: translated, color: getUserColor(translated) };
				}
				if ('formattedKey' in value) {
					const translated = translateToString(value.formattedKey.key, value.formattedKey.formats.map((replace, i) => [i.toString(), replace]));
					return { label: translated, color: getUserColor(translated) };
				}
				if ('float' in value) {
					const { min, max } = minMaxAdditionalColumn.find(minMax => minMax.id === id && field === minMax.field) ?? { min: 0, max: 0 };
					if (colorScale) {
						return scaledEntry(colorScale, value.float, rangeOnMap({ min, max }), v => getNumberFormatter().format(v));
					}
					// a single distinct value has no range to bucket: label it plainly
					if (min === max) {
						return { label: getNumberFormatter().format(value.float), color: 'rgb(5, 119, 57)' };
					}
					const steps = splitIntervalInEqualParts(min, max, 10);
					const { color, stepIndex } = getColorAndStepBySteps(value.float, steps, { r: 255, g: 255, b: 255 }, { r: 5, g: 119, b: 57 });
					return { label: translateToString('from_{{NAME1}}_to_{{NAME2}}', [['NAME1', stepIndex === 0 ? '-inf' : steps[stepIndex - 1].toFixed(2)], ['NAME2', steps[stepIndex].toFixed(2)]]), color };
				}
				if ('int' in value) {
					const { min, max } = minMaxAdditionalColumn.find(minMax => minMax.id === id) ?? { min: 0, max: 0 };
					if (colorScale) {
						return scaledEntry(colorScale, value.int, rangeOnMap({ min, max }), v => getNumberFormatter().format(v));
					}
					if (min === max) {
						return { label: getNumberFormatter().format(value.int), color: 'rgb(5, 119, 57)' };
					}
					const steps = splitIntervalInEqualParts(min, max, 10);
					const { color, stepIndex } = getColorAndStepBySteps(value.int, steps, { r: 255, g: 255, b: 255 }, { r: 5, g: 119, b: 57 });
					return { label: translateToString('from_{{NAME1}}_to_{{NAME2}}', [['NAME1', stepIndex === 0 ? '-inf' : steps[stepIndex - 1].toFixed(2)], ['NAME2', steps[stepIndex].toFixed(2)]]), color };
				}
				if ('colorDot' in value) {
					return { label: value.colorDot, color: value.colorDot };
				}
				if ('pieChart' in value) {
					//TODO pieChart
					return noDataOption;
				}
				if ('date' in value) {
					const range = moment().diff(moment.utc(Object.values(value.date.val)[0]), 'days');
					const color = getPinColorFromRange(range, rangeColor);
					return { label: color, color: color };
				}
				if ('datetime' in value) {
					const range = moment().diff(moment.unix(value.datetime), 'days');
					const color = getPinColorFromRange(range, rangeColor);
					return { label: color, color: color };
				}
				if ('user' in value) {
					//TODO user
					return noDataOption;
				}
				if ('evolution' in value && value.evolution.new.val && 'percentage' in value.evolution.new.val) {
					const { min, max } = minMaxAdditionalColumn.find(minMax => minMax.id === id && field === minMax.field) ?? { min: 0, max: 0 };
					if (colorScale) {
						return scaledEntry(colorScale, calcPercentage(value.evolution.new.val.percentage), rangeOnMap({ min, max }, PERCENT_RANGE), formatPercent);
					}
					const steps = [min, ...[2, 3, 4].map(n => min + max - ((max - min) / n)), max];
					const { color, stepIndex } = getColorAndStepBySteps(calcPercentage(value.evolution.new.val.percentage), steps, { r: 255, g: 255, b: 255 }, { r: 5, g: 119, b: 57 });
					return { label: translateToString('from_{{NAME1}}_to_{{NAME2}}', [['NAME1', stepIndex === 0 ? '-inf' : steps[stepIndex - 1].toFixed(2)], ['NAME2', steps[stepIndex].toFixed(2)]]), color };
				}
				if ('product' in value) {
					//TODO product
					return noDataOption;
				}
				if ('list' in value) {
					return value.list.map(p => ({ label: p, color: getUserColor(p) }));
				}

				return noDataOption;
			};
			break;
		default: return { data: [] };
	}
	return {
		data: points.map(pointToValue).reduce((acc: DataEntry[], val) => {
			const v = fn(val?.value, val?.name);
			const f = (val: FnReturn) => {
				const vInAcc = acc.find(c => c.label === val.label);
				if (!vInAcc) {
					acc.push({ ...val, value: 1 });
				} else {
					vInAcc.value += 1;
				}
			};
			if (Array.isArray(v)) {
				for (const item of v) {
					f(item);
				}
			} else {
				f(v);
			}
			return acc;
		}, []).sort((a, b) => {
			if (a.color == DEFAULT_MAP_PIN_COLOR) return 1;
			if (b.color == DEFAULT_MAP_PIN_COLOR) return -1;
			return b.value - a.value;
		}),
		noToolType
	};
}

function splitIntervalInEqualParts(left, right, parts) {
	const result: number[] = [];
	const delta = (right - left) / (parts - 1);
	while (left < right) {
		result.push(left);
		left += delta;
	}
	result.push(right);
	return result;
}

export type GenAdditionalColumnPinDataResult = {
	center?: boolean,
	outsideChild?: React.ReactNode,
	size?: number,
	color?: string,
	children?: React.ReactNode,
	child?: React.ReactNode,
	subTitle?: string,
	logo?: string
};

export function genAdditionalColumnPinData(
	value: IAdditionalColumnsSubValue | undefined,
	type: AdditionalColumnType | FieldType,
	rangeColor: { firstRange: number, lastRange: number },
	assortiments: AssortmentShort[],
	details: MapCompany,
	minMaxAdditionalColumn: MinMaxAdditionalColumn[],
	field: boolean,
	id: string,
	customPins?: Record<string, PinStyle>,
	displayOptions?: ColumnDisplayOptions,
): GenAdditionalColumnPinDataResult {
	const colorScale = displayOptions?.color_scale;
	if (value === undefined || value === null || value?.value === undefined || value?.value === null) return { color: DEFAULT_MAP_PIN_COLOR };
	let parsed;
	try {
		parsed = JSON.parse(value.value);
	} catch (_e) {
		parsed = value.value;
	}
	switch (type) {
		case 'Company':
			if (value.name === undefined || value.name === null) return { color: DEFAULT_MAP_PIN_COLOR };
			return { color: getUserColor(value.name), subTitle: value.name };
		case 'Boolean': {
			const colors = booleanColorsOf(displayOptions);
			if (parsed) return { color: colors.true, subTitle: translateToString('true') };
			return { color: colors.false, subTitle: translateToString('false') };
		}
		case 'Date': {
			const date = moment.utc(parsed);
			const color = colorScale
				? colorOf(colorScale, daysFromToday(parsed), daysOnMap(minMaxAdditionalColumn, id, field))
				: getColorForAdditionalColumnDate(parsed, minMaxAdditionalColumn, id, field, rangeColor);
			return { color, subTitle: date.format('L LT') };
		}
		case 'Catalogue': {
			const assortment = assortiments.find(a => a.id === parsed);
			return { color: getUserColor(assortment?.name ?? ''), subTitle: assortment?.name };
		}
		case 'Number':
		case 'Money':
		case 'Integer': {
			const { min, max, isMoney } = minMaxAdditionalColumn.find(minMax => minMax.id === id && field === minMax.field) ?? { min: 0, max: 0, isMoney: false };
			let color: string;
			if (colorScale) {
				const divideForMoney = (value: number) => isMoney ? value / 100 : value;
				color = colorOf(colorScale, divideForMoney(parsed), rangeOnMap({ min: divideForMoney(min), max: divideForMoney(max) }));
			} else if (min === max) {
				color = 'rgb(5, 119, 57)';
			} else {
				const steps = [min, ...[2, 3, 4].map(n => min + max - ((max - min) / n)), max];
				color = getColorAndStepBySteps(parsed, steps, { r: 255, g: 255, b: 255 }, { r: 5, g: 119, b: 57 }).color;
			}
			const formatter = isMoney ? getCurrencyFormatter() : getNumberFormatter();
			return { color, subTitle: formatter.format(isMoney ? parsed / 100 : parsed) };
		}
		case 'String':
		case 'Select': {
			const customPinStyle = customPins ? customPins[parsed.toString()] : undefined;
			if (customPinStyle?.logo) {
				return {
					color: customPinStyle?.color ?? getUserColor(parsed.toString()),
					logo: customPinStyle.logo,
					subTitle: parsed
				};
			}
			return { color: customPinStyle?.color ?? getUserColor(parsed.toString()), subTitle: parsed };
		}
		case 'MultiSelect':
		case 'Multiselect': {
			const size = 30;
			if (!Array.isArray(parsed)) return { color: DEFAULT_MAP_PIN_COLOR };
			const data = parsed.map(d => ({ label: d, color: customPins ? customPins[d]?.color ?? getUserColor(d) : getUserColor(d), value: 100 / parsed.length }));
			return {
				size,
				center: true,
				outsideChild: <MapToolTip data={data.filter(d => d.value > 0)} instore title={details.name ?? ''} />,
				child: <PieChart
					className='circle'
					lineWidth={70}
					center={[50, 50]}
					viewBoxSize={[100, 100]}
					data={data}
				/>
			};
		}
		case 'ReportColumn': {
			const value: ReportColumnType = parsed;

			if (typeof value === 'string' || !value) {
				return { color: DEFAULT_MAP_PIN_COLOR };
			}
			if ('percentage' in value) {
				const percentage = value.percentage[0] / value.percentage[1];
				if (colorScale) {
					const percent = percentOf(value.percentage);
					return { color: colorOf(colorScale, percent, rangeOnMap(minMaxAdditionalColumn.find(minMax => minMax.id === id && field === minMax.field), PERCENT_RANGE)), subTitle: formatPercent(percent) };
				}
				const steps = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
				const colors = ['#d44b39', '#F7685B', '#FF814B', '#FEA240', '#FEC940', '#FFEA2C', '#D2F34E', '#87F16D', '#2ED47A', '#44b772', '#45975a'];
				const { color } = getColorDiscreetSteps(percentage, steps, colors);
				return { color, subTitle: `${parseFloat((percentage * 100).toFixed(2))}%` };
			}
			if ('text' in value) {
				return { subTitle: value.text, color: getUserColor(value.text) };
			}
			if ('key' in value) {
				const translated = translateToString(value.key);
				return { subTitle: translated, color: getUserColor(translated) };
			}
			if ('formattedKey' in value) {
				const translated = translateToString(value.formattedKey.key, value.formattedKey.formats.map((replace, i) => [i.toString(), replace]));
				return { subTitle: translated, color: getUserColor(translated) };
			}
			if ('float' in value) {
				const { min, max } = minMaxAdditionalColumn.find(minMax => minMax.id === id && field === minMax.field) ?? { min: 0, max: 0 };
				if (colorScale) {
					return { color: colorOf(colorScale, value.float, rangeOnMap({ min, max })), subTitle: value.float.toString() };
				}
				// a single distinct value has no range: full color instead of the first bucket
				if (min === max) {
					return { color: 'rgb(5, 119, 57)', subTitle: value.float.toString() };
				}
				const steps = splitIntervalInEqualParts(min, max, 10);
				const { color } = getColorAndStepBySteps(value.float, steps, { r: 255, g: 255, b: 255 }, { r: 5, g: 119, b: 57 });
				return { color, subTitle: value.float.toString() };
			}
			if ('int' in value) {
				const { min, max } = minMaxAdditionalColumn.find(minMax => minMax.id === id) ?? { min: 0, max: 0 };
				if (colorScale) {
					return { color: colorOf(colorScale, value.int, rangeOnMap({ min, max })), subTitle: value.int.toString() };
				}
				if (min === max) {
					return { color: 'rgb(5, 119, 57)', subTitle: value.int.toString() };
				}
				const steps = splitIntervalInEqualParts(min, max, 10);
				const { color } = getColorAndStepBySteps(value.int, steps, { r: 255, g: 255, b: 255 }, { r: 5, g: 119, b: 57 });
				return { color, subTitle: value.int.toString() };
			}
			if ('colorDot' in value) {
				return { color: value.colorDot };
			}
			if ('pieChart' in value) {
				//TODO pieChart
				return { color: DEFAULT_MAP_PIN_COLOR };
			}
			if ('date' in value) {
				const date = moment.utc(moment.utc(Object.values(value.date.val)[0]));
				const range = moment().diff(date, 'days');
				return { color: getPinColorFromRange(range, rangeColor), subTitle: date.format('L LT') };
			}
			if ('datetime' in value) {
				const date = moment.utc(moment.unix(value.datetime));
				const range = moment().diff(date, 'days');
				return { color: getPinColorFromRange(range, rangeColor), subTitle: date.format('L LT') };
			}
			if ('user' in value) {
				//TODO user
				return { color: DEFAULT_MAP_PIN_COLOR };
			}
			if ('evolution' in value && value.evolution.new.val && 'percentage' in value.evolution.new.val) {
				const { min, max } = minMaxAdditionalColumn.find(minMax => minMax.id === id && field === minMax.field) ?? { min: 0, max: 0 };
				if (colorScale) {
					const percent = calcPercentage(value.evolution.new.val.percentage);
					return { color: colorOf(colorScale, percent, rangeOnMap({ min, max }, PERCENT_RANGE)), subTitle: formatPercent(percent) };
				}
				const steps = [min, ...[2, 3, 4].map(n => min + max - ((max - min) / n)), max];
				const { color } = getColorAndStepBySteps(calcPercentage(value.evolution.new.val.percentage), steps, { r: 255, g: 255, b: 255 }, { r: 5, g: 119, b: 57 });
				return { color, subTitle: value.evolution.toString() };
			}
			if ('product' in value) {
				//TODO product
				return { color: DEFAULT_MAP_PIN_COLOR };
			}
			if ('list' in value) {
				const data = value.list.map(d => ({ label: d, color: getUserColor(d), value: 100 / value.list.length }));
				return {
					child: <div style={{
						width: '30px',
						transform: 'translate(-15px, -15px)'
					}}>
						<PieChart
							className='circle'
							lineWidth={70}
							center={[50, 50]}
							viewBoxSize={[100, 100]}
							data={data}
						/>
						<MapToolTip data={data.filter(d => d.value > 0)} instore title={details.name ?? ''} />
					</div>
				};
			}

			return { color: DEFAULT_MAP_PIN_COLOR };
		}
		default: return { color: DEFAULT_MAP_PIN_COLOR };
	}
}

type ColorRGB = {
	r: number;
	g: number;
	b: number;
};

export function getStepColors(start: ColorRGB, end: ColorRGB, step: number, maxSteps: number): string {
	return getColorForPercentage((step + 1) / maxSteps, 1, [{ pct: 0, color: start }, { pct: 1, color: end }]);
}

function getColorAndStepBySteps(value: number, steps: number[], start: ColorRGB, end: ColorRGB): { color: string, stepIndex: number } {
	let index = steps.findIndex(s => value < s);
	if (index < 0) {
		index = steps.length - 1;
	}
	const stepValue = index / (steps.length - 1);
	return {
		color: getColorForPercentage(stepValue, 1, [{ pct: 0, color: start }, { pct: 1, color: end }]),
		stepIndex: index
	};
}

function getColorDiscreetSteps(value: number, steps: number[], colors: string[]): { color: string, stepIndex: number } {
	let index = steps.findIndex(s => value < s);
	if (index < 0) {
		index = steps.length - 1;
	}
	if (index >= colors.length) {
		index = colors.length - 1;
	}
	return {
		color: colors[index],
		stepIndex: index
	};
}
